import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, Building2, CreditCard, Calendar, Clock, Loader2, AlertCircle, CheckCircle, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERVAL = { one_time: "One-time", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

export default function AgreementPage() {
  const { token } = useParams();
  const [agr, setAgr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acceptName, setAcceptName] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agreement/${token}`)
      .then(r => { setAgr(r.data); if (r.data.accepted_at) setAccepted(true); })
      .catch(() => setError("Agreement not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!acceptName.trim()) return alert("Please enter your name to accept");
    setAccepting(true);
    try {
      await axios.post(`${API}/agreement/${token}/accept`, { name: acceptName.trim() });
      setAccepted(true);
      setAgr(prev => ({ ...prev, accepted_at: new Date().toISOString(), accepted_by: acceptName.trim() }));
    } catch (e) { alert("Failed to accept. Please try again."); }
    setAccepting(false);
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" /></div>;
  if (error) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="text-center"><AlertCircle className="w-12 h-12 text-neutral-600 mx-auto mb-4" /><p className="text-neutral-500">{error}</p></div></div>;

  const c = agr.customer || {};
  const p = agr.provider || {};

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="agreement-page">
      <nav className="border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-outfit font-medium text-white text-lg tracking-tight">wedowedo</span>
          <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600">AGREEMENT</span>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-outfit font-bold text-3xl text-white mb-2">{agr.title}</h1>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${agr.status === "active" ? "bg-green-500/10 text-green-400" : agr.status === "cancelled" ? "bg-red-500/10 text-red-400" : "bg-blue-500/10 text-blue-400"}`}>{agr.status}</span>
            <span className="text-xs text-neutral-600">{agr.type?.replace("_", " ")}</span>
          </div>
        </div>

        {/* Parties */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="bg-[#0a0a0a] border border-white/5 rounded-sm p-6">
            <span className="font-mono text-[10px] tracking-[0.15em] text-neutral-600 block mb-4">PROVIDER</span>
            <p className="text-white font-medium">{p.name}</p>
            <p className="text-sm text-neutral-500 mt-1">Reg: {p.reg}</p>
            <p className="text-sm text-neutral-500">VAT: {p.vat}</p>
            <p className="text-sm text-neutral-500">{p.address}</p>
            <p className="text-sm text-neutral-500">{p.email}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/5 rounded-sm p-6">
            <span className="font-mono text-[10px] tracking-[0.15em] text-neutral-600 block mb-4">CUSTOMER</span>
            <p className="text-white font-medium">{c.company_name || "—"}</p>
            <p className="text-sm text-neutral-500 mt-1">{c.contact_name || ""}</p>
            {c.org_number && <p className="text-sm text-neutral-500">Org: {c.org_number}</p>}
            {c.country && <p className="text-sm text-neutral-500">Country: {c.country}</p>}
            {c.billing_address && <p className="text-sm text-neutral-500">{c.billing_address}</p>}
          </div>
        </div>

        {/* Terms */}
        <div className="bg-[#0a0a0a] border border-white/5 rounded-sm p-6 mb-6">
          <span className="font-mono text-[10px] tracking-[0.15em] text-neutral-600 block mb-6">AGREEMENT TERMS</span>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 gap-x-8">
            <div>
              <span className="text-xs text-neutral-600 block mb-1">Amount</span>
              <span className="text-xl text-white font-mono font-bold">{agr.amount?.toLocaleString("sv-SE", { minimumFractionDigits: 2 })} {agr.currency}</span>
            </div>
            <div>
              <span className="text-xs text-neutral-600 block mb-1">Billing</span>
              <span className="text-sm text-white">{INTERVAL[agr.billing_interval] || agr.billing_interval}</span>
            </div>
            {agr.auto_charge && (
              <div>
                <span className="text-xs text-neutral-600 block mb-1">Payment</span>
                <span className="text-sm text-white flex items-center gap-1"><CreditCard className="w-3.5 h-3.5 text-[#00F0FF]" /> Auto-debit</span>
              </div>
            )}
            {agr.start_date && (
              <div>
                <span className="text-xs text-neutral-600 block mb-1">Start date</span>
                <span className="text-sm text-white">{agr.start_date}</span>
              </div>
            )}
            {agr.end_date && (
              <div>
                <span className="text-xs text-neutral-600 block mb-1">End date</span>
                <span className="text-sm text-white">{agr.end_date}</span>
              </div>
            )}
            {agr.binding_period && (
              <div>
                <span className="text-xs text-neutral-600 block mb-1">Binding period</span>
                <span className="text-sm text-white">{agr.binding_period}</span>
              </div>
            )}
            {agr.notice_period && (
              <div>
                <span className="text-xs text-neutral-600 block mb-1">Notice period</span>
                <span className="text-sm text-white">{agr.notice_period}</span>
              </div>
            )}
          </div>
        </div>

        {/* Description / Notes */}
        {(agr.description || agr.notes) && (
          <div className="bg-[#0a0a0a] border border-white/5 rounded-sm p-6 mb-6">
            {agr.description && (
              <div className="mb-4">
                <span className="font-mono text-[10px] tracking-[0.15em] text-neutral-600 block mb-3">DESCRIPTION</span>
                <p className="text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap">{agr.description}</p>
              </div>
            )}
            {agr.notes && (
              <div>
                <span className="font-mono text-[10px] tracking-[0.15em] text-neutral-600 block mb-3">NOTES</span>
                <p className="text-sm text-neutral-400 leading-relaxed whitespace-pre-wrap">{agr.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Acceptance section */}
        {agr.acceptance_required && !accepted && (
          <div className="bg-[#0a0a0a] border border-[#00F0FF]/20 rounded-sm p-6 mb-6" data-testid="accept-section">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-[#00F0FF]" />
              <span className="font-outfit font-medium text-white">Accept this agreement</span>
            </div>
            <p className="text-sm text-neutral-400 mb-4">By entering your name and clicking accept, you confirm that you have reviewed and agree to the terms outlined in this agreement.</p>
            <div className="flex gap-3">
              <input
                value={acceptName}
                onChange={e => setAcceptName(e.target.value)}
                placeholder="Your full name"
                data-testid="accept-name-input"
                className="flex-1 bg-black border border-white/10 text-white px-4 py-3 rounded-sm text-sm focus:border-[#00F0FF] outline-none"
              />
              <button
                onClick={handleAccept}
                disabled={accepting || !acceptName.trim()}
                data-testid="accept-agreement-btn"
                className="flex items-center gap-2 bg-[#00F0FF] text-black px-6 py-3 rounded-sm text-sm font-medium hover:shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all disabled:opacity-50"
              >
                {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Accept
              </button>
            </div>
          </div>
        )}

        {accepted && (
          <div className="bg-green-500/5 border border-green-500/20 rounded-sm p-6 mb-6 text-center" data-testid="accepted-confirmation">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-3" />
            <p className="text-green-400 font-medium mb-1">Agreement accepted</p>
            <p className="text-sm text-neutral-500">
              {agr.accepted_at ? `Accepted on ${agr.accepted_at.slice(0, 10)}` : "Just now"}
              {agr.accepted_by ? ` by ${agr.accepted_by}` : ""}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-10 pt-8 border-t border-white/5">
          <p className="text-xs text-neutral-700 font-mono">{p.name} &middot; Reg: {p.reg} &middot; VAT: {p.vat} &middot; {p.address}</p>
          <p className="text-xs text-neutral-800 mt-1">Document generated {agr.created_at?.slice(0, 10)}</p>
        </div>
      </div>
    </div>
  );
}
